import {
  AuthChangePasswordRequest,
  AuthLoginPostResponse,
  LoginApiResponse,
  LoginRequest,
  LoginResponse,
  LogoutReason,
  MessageResponse,
  mapLoginResponse,
} from '@/types/auth.types';
import type { GenericErrorResponse } from '@/types/api.types';
import { request } from '@/services/request';
import { collectClientInfo } from '@/lib/clientInfo';
import { getLastActivityAt, inactivityTimedOutAt } from '@/services/lastActivity';
import { useUserStore } from '@/stores/user';
import { useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';

const LOGOUT_TIMEOUT_MS = 3000;

export const authService = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const clientInfo = await collectClientInfo();
    const { data } = await axiosInstance.post<LoginApiResponse>('/auth/login', {
      email: email.trim(),
      password,
      clientInfo,
    });
    return mapLoginResponse(data);
  },

  /**
   * Reports the end of the login session for the log-in history (DGR-1835).
   * The session token and logout time are read synchronously, so the caller
   * can clear the store straight after calling this. It never rejects.
   *
   * The web owns the inactivity limit, so it works out when the session ended:
   * INACTIVITY at last activity + the limit, BROWSER_CLOSED at last activity,
   * otherwise the API uses now.
   */
  logout: (reason: LogoutReason): Promise<void> => {
    const loginSessionToken = useUserStore.getState().loginSessionToken;
    if (!loginSessionToken) return Promise.resolve();
    const loggedOutAt =
      reason === 'INACTIVITY' ? inactivityTimedOutAt() : reason === 'BROWSER_CLOSED' ? getLastActivityAt() : undefined;

    return axiosInstance
      .post(
        '/auth/logout',
        { loginSessionToken, reason, ...(loggedOutAt && { loggedOutAt }) },
        { timeout: LOGOUT_TIMEOUT_MS }
      )
      .then(() => undefined)
      .catch(() => undefined);
  },
};

export const useAuthLogin = () =>
  useMutation<AuthLoginPostResponse, GenericErrorResponse, { data: LoginRequest }>({
    mutationFn: ({ data }) => request(() => axiosInstance.post<AuthLoginPostResponse>('/auth/login', data)),
  });

export const useForgotPassword = () =>
  useMutation<MessageResponse, GenericErrorResponse, { email: string }>({
    mutationFn: ({ email }) =>
      request(() => axiosInstance.post<MessageResponse>(`/auth/forgot-password/${encodeURIComponent(email)}`)),
  });

/** Validates an onboarding (invite) link payload. Errors when the link is invalid or expired. */
export const useOnboardPayload = (payload: string | undefined, options?: { enabled?: boolean }) =>
  useQuery<MessageResponse, GenericErrorResponse>({
    queryKey: ['auth', 'onboard', payload],
    queryFn: () =>
      request(() => axiosInstance.get<MessageResponse>(`/auth/onboard/${encodeURIComponent(String(payload))}`)),
    // No payload guard: a missing payload must fail so the page shows the expired-link state.
    enabled: options?.enabled ?? true,
  });

/** Validates a forgot-password link payload. Errors when the link is invalid or expired. */
export const useResetPasswordPayload = (payload: string | undefined, options?: { enabled?: boolean }) =>
  useQuery<MessageResponse, GenericErrorResponse>({
    queryKey: ['auth', 'reset-password', payload],
    queryFn: () =>
      request(() => axiosInstance.get<MessageResponse>(`/auth/reset-password/${encodeURIComponent(String(payload))}`)),
    // No payload guard: a missing payload must fail so the page shows the expired-link state.
    enabled: options?.enabled ?? true,
  });

export const useOnboard = () =>
  useMutation<MessageResponse, GenericErrorResponse, { data: AuthChangePasswordRequest }>({
    mutationFn: ({ data }) => request(() => axiosInstance.post<MessageResponse>('/auth/onboard', data)),
  });

export const useConfirmForgotPassword = () =>
  useMutation<MessageResponse, GenericErrorResponse, { data: AuthChangePasswordRequest }>({
    mutationFn: ({ data }) =>
      request(() => axiosInstance.post<MessageResponse>('/auth/confirm-forgot-password', data)),
  });
